import API from 'services/api';
import {
  IArchivalExportRow,
  IArchivalQueuedResponse,
  IArchivalRecordQuery,
  IArchivalRequestRow,
  IArchivedRecord,
  ICreateArchivalExport,
  ICreateArchivalRequest,
} from 'core/models/archival';

/**
 * Archival APIs, as built in reportsv2-gopher (branch feat/data-archival) - see
 * SIERA-ARCHIVAL-API-CONTEXT.md §4.
 *
 * Payloads and responses are camelCase, unlike the rest of this app's APIs.
 *
 * The namespace dropdown is not an API call: archival reads no namespace config,
 * so the options are the hardcoded list in archival.constants.
 */
export class ArchivalService extends API {
  static baseURL = process.env.REACT_APP_DATA_BASE_URL || '';

  constructor() {
    super(ArchivalService.baseURL);
  }

  // POST dataapi/archival/create-request
  createArchivalRequest = async (payload: ICreateArchivalRequest): Promise<IArchivalQueuedResponse> => (
    this.responsify(await this.instance.post('archival/create-request', payload))
  );

  // GET dataapi/archival/get-requests - tenant's requests, newest first, cap 50.
  // Rows carry current status, so this doubles as the status read.
  getArchivalRequests = async (): Promise<Array<IArchivalRequestRow>> => (
    this.responsify(await this.instance.get('archival/get-requests'))
  );

  // POST dataapi/archival/create-export
  createArchivalExport = async (payload: ICreateArchivalExport): Promise<IArchivalQueuedResponse> => (
    this.responsify(await this.instance.post('archival/create-export', payload))
  );

  // GET dataapi/archival/get-exports - signed-in user's exports, newest first, cap 20
  getArchivalExports = async (): Promise<Array<IArchivalExportRow>> => (
    this.responsify(await this.instance.get('archival/get-exports'))
  );

  /**
   * GET dataapi/archival/get-record - one archived record.
   *
   * The contract is agreed but the route is not built yet, so this returns 404
   * today and the screen falls back to its "not found" state. `id` is the source
   * system's primary key, not an archival request id.
   */
  getArchivedRecord = async (query: IArchivalRecordQuery): Promise<IArchivedRecord> => (
    this.responsify(await this.instance.get('archival/get-record', {
      params: {
        namespace: query.namespace,
        namespaceType: query.namespaceType,
        id: query.id,
      },
    }))
  );
}

export default ArchivalService;
